import React, { useState, useEffect } from 'react';
import { X, MapPin, Briefcase, Clock, AlertCircle, Loader2, CheckCircle, MessageSquare } from 'lucide-react';
import { axiosInstance as axios } from '../../context/AuthContext';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const RequestProfessionalModal = ({ isOpen, onClose, onSuccess, initialSpecialty = '', initialCity = '' }) => {
  const [formData, setFormData] = useState({
    specialty: '',
    city: '',
    region: '',
    country: 'USA',
    urgency: 'normal',
    notes: ''
  });
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchSpecialties();
      setFormData({
        specialty: initialSpecialty,
        city: initialCity,
        region: '',
        country: 'USA',
        urgency: 'normal',
        notes: ''
      });
      setSuccess(false);
      setError('');
    }
  }, [isOpen, initialSpecialty, initialCity]);

  const fetchSpecialties = async () => {
    try {
      const response = await axios.get(`${API}/professional-invites/specialties`);
      setSpecialties(response.data.specialties || []);
    } catch (err) {
      console.error('Error fetching specialties:', err);
      setSpecialties([
        { value: 'financial_planner', label: 'Financial Planner', phase: 1 },
        { value: 'estate_planner', label: 'Estate Planner', phase: 1 },
        { value: 'attorney', label: 'Attorney', phase: 1 },
        { value: 'genealogist', label: 'Genealogist', phase: 2 },
        { value: 'family_counselor', label: 'Family Counselor', phase: 2 },
        { value: 'mediator', label: 'Mediator', phase: 2 }
      ]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/professional-requests`, formData, {
        withCredentials: true
      });
      
      setSuccess(true);
      
      setTimeout(() => {
        onSuccess && onSuccess(response.data);
        onClose();
      }, 2000);
      
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError((typeof detail === 'string' ? detail : detail?.message) || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="request-professional-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Request a Professional</h2>
            <p className="text-emerald-100 text-sm">Let us know what you need in your area</p>
          </div>
          <button 
            onClick={onClose}
            className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition"
            data-testid="close-request-modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Success State */}
        {success ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-green-600" size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Request Submitted!</h3>
            <p className="text-gray-600">
              We've recorded your need for a <strong>{specialties.find(s => s.value === formData.specialty)?.label}</strong> in <strong>{formData.city}</strong>. 
              We'll notify you when a matching professional joins!
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Info Box */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <p className="text-emerald-800 text-sm">
                <strong>How it works:</strong> Your request helps us prioritize outreach to professionals in your area. 
                The more families that request a specialty, the higher priority it becomes!
              </p>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={18} />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Specialty */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                What type of professional do you need? *
              </label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <select
                  name="specialty"
                  value={formData.specialty}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none bg-white"
                  data-testid="request-specialty-select"
                >
                  <option value="">Select a specialty...</option>
                  <optgroup label="Phase 1 - Priority">
                    {specialties.filter(s => s.phase === 1).map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Phase 2 - Coming Soon">
                    {specialties.filter(s => s.phase === 2).map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
            </div>

            {/* Location Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City *
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Atlanta"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    data-testid="request-city-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State/Region
                </label>
                <input
                  type="text"
                  name="region"
                  value={formData.region}
                  onChange={handleChange}
                  placeholder="e.g., GA"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="request-region-input"
                />
              </div>
            </div>

            {/* Urgency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                How soon do you need this? *
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'normal', label: 'No Rush', icon: '🕐', desc: 'Within months' },
                  { value: 'soon', label: 'Soon', icon: '📅', desc: 'Within weeks' },
                  { value: 'urgent', label: 'Urgent', icon: '⚡', desc: 'ASAP' }
                ].map(option => (
                  <label
                    key={option.value}
                    className={`relative flex flex-col items-center p-3 border-2 rounded-lg cursor-pointer transition ${
                      formData.urgency === option.value
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="urgency"
                      value={option.value}
                      checked={formData.urgency === option.value}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className="text-2xl mb-1">{option.icon}</span>
                    <span className={`font-medium text-sm ${formData.urgency === option.value ? 'text-emerald-700' : 'text-gray-700'}`}>
                      {option.label}
                    </span>
                    <span className="text-xs text-gray-500">{option.desc}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Details (Optional)
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 text-gray-400" size={18} />
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Any specific requirements or preferences..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                  data-testid="request-notes-input"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition"
                data-testid="cancel-request-btn"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg hover:from-emerald-700 hover:to-teal-700 font-medium transition flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="submit-request-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    Submit Request
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default RequestProfessionalModal;
